import React, { useCallback, useEffect, useRef } from "react";
import HCLTreeMxGraphView from "hcl-web-editor/app/components/Model/SingleHCLModelView/SingleTreeView/MxGraphViews/HCLTreeMxGraphView";

// Padding kept between the graph and the top/left edge when it can't be centred
// (i.e. the graph is larger than the visible area on that axis).
const TOP_MARGIN = 20;

/**
 * Wraps HCLTreeMxGraphView to keep the graph centred in its container.
 *   1. on load, poll (rAF) until the bounds settle, then centre, and
 *   2. re-centre on every container resize (modal drag-resize),
 * using a relative shift of the current graph bounds to the container centre,
 * which is independent of the library's internal offset and the tree's margin.
 */
const CenteredTreeMxGraphView = (props: any) => {
  const viewRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Shift the already-rendered graph so its current bounds sit at the centre of
  // the container. Returns false (a no-op) until the graph + bounds are ready.
  const centerGraph = useCallback((): boolean => {
    const instance = viewRef.current;
    const graph = instance && instance.graph;
    const container = containerRef.current;
    // targetCellKey means the library centres on a specific cell instead.
    if (!graph || !container || props.targetCellKey) return false;

    const bounds = graph.getGraphBounds();
    if (!(bounds.width > 0)) return false;

    // getGraphBounds() is in screen coordinates (current scale + translate
    // applied), so we move it relative to where it already is.
    const scale = graph.view.getScale() || 1;
    const t = graph.view.getTranslate();
    const desiredX = Math.max(
      TOP_MARGIN,
      (container.clientWidth - bounds.width) / 2
    );
    const desiredY = Math.max(
      TOP_MARGIN,
      (container.clientHeight - bounds.height) / 2
    );
    graph.view.setTranslate(
      t.x + (desiredX - bounds.x) / scale,
      t.y + (desiredY - bounds.y) / scale
    );
    graph.view.refresh();
    return true;
  }, [props.targetCellKey]);

  // Initial centering: poll until the layout animation has settled, then centre.
  useEffect(() => {
    if (props.targetCellKey) return undefined;

    let rafId = 0;
    let attempts = 0;
    let prevWidth = -1;

    const tick = () => {
      const graph = viewRef.current && viewRef.current.graph;
      if (graph) {
        // Two consecutive equal, non-zero readings => bounds computed & stable.
        const width = graph.getGraphBounds().width;
        const settled = width > 0 && width === prevWidth;
        prevWidth = width;
        if (settled) {
          centerGraph();
          return;
        }
      }
      if (attempts < 90) {
        attempts += 1;
        rafId = requestAnimationFrame(tick);
      }
    };

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [props.data, props.targetCellKey, centerGraph]);

  // Re-centre whenever the container changes size (e.g. the modal is resized).
  // Re-centering only mutates the graph's internal view translate, never the
  // container box, so this cannot feed back into the observer.
  useEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === "undefined") return undefined;

    const observer = new ResizeObserver(() => {
      centerGraph();
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [centerGraph]);

  return (
    <div
      ref={containerRef}
      style={{ width: "100%", height: "100%", display: "flex" }}
    >
      <HCLTreeMxGraphView ref={viewRef} {...props} />
    </div>
  );
};

export default CenteredTreeMxGraphView;
